import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Check, ChevronDown, Star } from "lucide-react";
import { api } from "../lib/data";
import { useSEO } from "../lib/useSEO";
import { FAQ } from "../lib/faq";
import { PageDecor } from "../components/decor/PageDecor";
import { formatSwissPhone, isValidEmail } from "../lib/formHelpers";
import { EmailSuggestion } from "../components/EmailSuggestion";


export default function Contact() {
  useSEO({
    title: "Contact et FAQ — 1000 ARTS · Chemin des Dailles 10E, Cugy VD",
    description: "Contactez 1000 ARTS : +41 79 950 40 31, info1000arts@gmail.com, Chemin des Dailles 10E, 1053 Cugy VD. Réponses aux questions fréquentes (tarifs, inscription, spectacle, rabais famille).",
    canonical: "/contact",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [openIdx, setOpenIdx] = useState(0);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "phone" ? formatSwissPhone(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(form.email)) {
      setError("Merci de vérifier ton adresse e-mail (ex : prenom.nom@exemple.ch).");
      return;
    }
    setLoading(true); setError("");
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-contact" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
      <div className="eyebrow mb-3">Une question ?</div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight">
        Contacte-<span style={{ color: "var(--primary)" }}>nous</span>
      </h1>

      <div data-testid="contact-reviews" className="hidden"></div>

      <div className="grid lg:grid-cols-2 gap-12 mt-14">
        <form onSubmit={submit} className="space-y-5 card-flat" data-testid="contact-form">
          <div className="font-display text-2xl mb-2">Envoie-nous un message</div>
          <div><label className="field-label">Nom *</label><input data-testid="contact-input-name" required className="input-field" value={form.name} onChange={update("name")} /></div>
          <div><label className="field-label">E-mail *</label><input data-testid="contact-input-email" type="email" required className="input-field" value={form.email} onChange={update("email")} /><EmailSuggestion value={form.email} onFix={(v) => setForm({ ...form, email: v })} testid="contact-email-suggestion" /></div>
          <div><label className="field-label">Téléphone <span className="text-[var(--muted)] font-normal">(optionnel)</span></label><input data-testid="contact-input-phone" type="tel" inputMode="tel" className="input-field" placeholder="+41 79 950 40 31" value={form.phone} onChange={update("phone")} /></div>
          <div><label className="field-label">Message *</label><textarea data-testid="contact-input-message" required rows={5} className="input-field" value={form.message} onChange={update("message")} /></div>
          {error && <p data-testid="contact-error" className="text-sm" style={{ color: "#DC2626" }}>{error}</p>}
          {success && (
            <div data-testid="contact-success" className="flex items-center gap-2 p-4 rounded-2xl text-sm" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
              <Check size={18} /> Message envoyé · merci !
            </div>
          )}
          <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-primary">
            {loading ? "Envoi..." : "Envoyer le message"}
          </button>
        </form>

        <div className="space-y-6">
          <div className="card-flat">
            <div className="eyebrow mb-2">Studio</div>
            <p className="text-lg flex items-start gap-3"><MapPin size={20} style={{ color: "var(--primary)", marginTop: 4 }} /> <span>Chemin des Dailles 10E<br />1053 Cugy · Suisse</span></p>
          </div>
          <div className="card-flat">
            <div className="eyebrow mb-2">Téléphone</div>
            <p className="flex items-center gap-3 mb-1"><Phone size={18} style={{ color: "var(--primary)" }} /> <a href="tel:+41799504031" className="hover:underline" data-testid="contact-tel-danse">Danse : 079 950 40 31</a></p>
            <p className="flex items-center gap-3"><Phone size={18} style={{ color: "var(--primary)" }} /> <a href="tel:+41794368939" className="hover:underline" data-testid="contact-tel-comedie">Comédie musicale : 079 436 89 39</a></p>
          </div>
          <div className="card-flat">
            <div className="eyebrow mb-2">E-mail</div>
            <p className="flex items-center gap-3"><Mail size={18} style={{ color: "var(--primary)" }} /> <a href="mailto:info1000arts@gmail.com" className="hover:underline break-all" data-testid="contact-mail">info1000arts@gmail.com</a></p>
          </div>
          <div className="card-flat" data-testid="contact-demarches">
            <div className="eyebrow mb-2">Démarches en ligne</div>
            <p className="text-sm text-[var(--muted)] leading-relaxed">
              Deux choses se règlent en une minute, sans nous écrire&nbsp;:
            </p>
            <ul className="text-sm mt-3 space-y-2">
              <li>
                <Link to="/mon-avs" className="underline hover:text-[var(--primary)]" data-testid="contact-lien-avs">
                  Transmettre le n° AVS
                </Link>
                <span className="text-[var(--muted)]"> — pour le dossier Jeunesse+Sport et l'assurance</span>
              </li>
              <li>
                <Link to="/desinscription" className="underline hover:text-[var(--primary)]" data-testid="contact-lien-desinscription">
                  Arrêter les cours
                </Link>
                <span className="text-[var(--muted)]"> — l'écran indique aussitôt si vous êtes dans les délais</span>
              </li>
            </ul>
          </div>
          <div className="aspect-video rounded-2xl overflow-hidden border border-[var(--border-soft)]">
            <iframe title="Carte" src="https://www.google.com/maps?q=Chem.+des+Dailles+10E,+1053+Cugy,+Switzerland&output=embed" className="w-full h-full" style={{ border: 0 }} loading="lazy" />
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className="mt-28" data-testid="contact-faq">
        <div className="eyebrow mb-3">Questions fréquentes</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-10">On répond <span style={{ color: "var(--primary)" }}>à tout</span></h2>
        <div className="space-y-2">
          {FAQ.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={i} className="card-flat" data-testid={`faq-${i}`} style={{ padding: 0 }}>
                <button onClick={() => setOpenIdx(open ? -1 : i)} data-testid={`faq-toggle-${i}`} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-display text-lg pr-4">{item.q}</span>
                  <ChevronDown size={20} style={{ color: "var(--primary)", transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "" }} />
                </button>
                {open && <div className="px-5 pb-5 text-[var(--muted)] text-sm leading-relaxed">{item.a}</div>}
              </div>
            );
          })}
        </div>
      </section>
      </div>
    </div>
  );
}
